import type { IHighlight } from 'react-pdf-highlighter';
import type {
  ChatSourceRect,
  RoughNotationBracket,
  StandoutMarker,
} from '@libertify/ui';
import type { Cuepoint } from '../types';

export interface BuildSourceHighlightInput {
  id: string;
  quote: string;
  pageNumber: number;
  boundingRect: ChatSourceRect;
  rects: ChatSourceRect[];
  color: string;
  documentId: string;
  /**
   * How to draw the mark. Omitted → the swipe a chatbot citation draws, in
   * `color`; a marker brings its own color and wins over it.
   */
  marker?: StandoutMarker;
}

type MarkAnimation = {
  animate: boolean;
  animationDelay: number;
  /** rough-notation treats 0 as "appear at once", even with `animate` on. */
  animationDuration: number;
};

type Mark = StandoutMarker & MarkAnimation;

/** A chatbot citation's mark: a plain fill, no stroke, shown at once. */
const CITATION_MARK: Omit<Mark, 'color'> = {
  notation: 'highlight',
  strokeWidth: 0,
  padding: 0,
  iterations: 0,
  multiline: true,
  animate: false,
  animationDelay: 500,
  animationDuration: 0,
};

/**
 * A standout's mark draws itself in, the way the video-sync marks do (same
 * duration as the animator's default rough-notation). Used when the BE sent
 * no timing of its own.
 */
export const STANDOUT_ANIMATION: MarkAnimation = {
  animate: true,
  animationDelay: 500,
  animationDuration: 800,
};

/**
 * Pure transform: a source's positional zone → the `{ highlight, cuepoint }`
 * pair the PdfViewer needs (a highlight only renders when a matching cuepoint
 * exists). Extracted from the legacy `useSourceHandlers.prepareSource` so the
 * chatbot reference cards and the legacy viewer share one source of truth.
 * Coordinates are PDF-space (`usePdfCoordinates: true`).
 */
export const buildSourceHighlight = ({
  id,
  quote,
  pageNumber,
  boundingRect,
  rects,
  color,
  documentId,
  marker,
}: BuildSourceHighlightInput): {
  highlight: IHighlight;
  cuepoint: Cuepoint;
} => {
  const mark: Mark = marker
    ? {
        ...marker,
        animate: STANDOUT_ANIMATION.animate,
        animationDelay:
          marker.animationDelay ?? STANDOUT_ANIMATION.animationDelay,
        animationDuration:
          marker.animationDuration ?? STANDOUT_ANIMATION.animationDuration,
      }
    : { ...CITATION_MARK, color };

  const highlight = {
    content: { text: quote, image: '-' },
    position: {
      usePdfCoordinates: true,
      boundingRect,
      rects,
      pageNumber,
    },
    comment: { text: '', emoji: '' },
    id,
  } as unknown as IHighlight;

  const cuepoint = {
    title: '',
    source: 'source',
    roughNotation: {
      animate: mark.animate,
      type: mark.notation,
      animationDelay: mark.animationDelay,
      animationDuration: mark.animationDuration,
      strokeWidth: mark.strokeWidth,
      padding: mark.padding,
      color: mark.color,
      brackets: mark.brackets ?? (['left'] as RoughNotationBracket[]),
      iterations: mark.iterations,
      multiline: mark.multiline,
      show: true,
    },
    start: 0,
    highlightId: id,
    documentId,
    page: 0,
    top: 0,
  } as unknown as Cuepoint;

  return { highlight, cuepoint };
};
